import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/hooks/use-toast";
import { motion, AnimatePresence } from "framer-motion";
import {
  Building2, FileText, Scale, Landmark, Users, MapPin, FileCheck,
  CheckCircle2, Circle, ChevronRight, ChevronLeft, Rocket, Upload,
  BriefcaseBusiness, CreditCard, ArrowRight, Plus, Sparkles
} from "lucide-react";
import MacBookAnimation from "@/components/MacBookAnimation";

const STEPS = [
  { id: 1, label: "Mon projet", icon: Rocket, description: "Définir votre activité" },
  { id: 2, label: "Forme juridique", icon: Scale, description: "Choisir votre statut" },
  { id: 3, label: "Capital social", icon: CreditCard, description: "Montant & répartition" },
  { id: 4, label: "Siège social", icon: MapPin, description: "Adresse de l'entreprise" },
  { id: 5, label: "Dirigeant", icon: Users, description: "Informations du gérant" },
  { id: 6, label: "Options fiscales", icon: FileText, description: "IS/IR, TVA" },
  { id: 7, label: "Documents", icon: Upload, description: "Pièces justificatives" },
  { id: 8, label: "Immatriculation", icon: CheckCircle2, description: "Dépôt au Greffe" },
];

const LEGAL_FORMS = [
  { value: "sas", label: "SAS", desc: "Société par Actions Simplifiée" },
  { value: "sarl", label: "SARL", desc: "Société à Responsabilité Limitée" },
  { value: "sasu", label: "SASU", desc: "SAS Unipersonnelle" },
  { value: "eurl", label: "EURL", desc: "Entreprise Unipersonnelle à RL" },
  { value: "ei", label: "EI", desc: "Entreprise Individuelle" },
  { value: "auto", label: "Auto-entrepreneur", desc: "Micro-entreprise" },
];

type CreationData = {
  id?: string;
  company_name: string;
  business_activity: string;
  legal_form: string;
  capital_amount: number;
  capital_distribution: { name: string; percent: number }[];
  head_office_address: string;
  head_office_city: string;
  head_office_postal_code: string;
  director_info: { first_name: string; last_name: string; birth_date: string; nationality: string; };
  fiscal_option_is: boolean;
  fiscal_option_tva: boolean;
  current_step: number;
  steps_completed: number[];
  documents: Record<string, string>;
  status: string;
};

const defaultData: CreationData = {
  company_name: "", business_activity: "", legal_form: "",
  capital_amount: 1000, capital_distribution: [],
  head_office_address: "", head_office_city: "", head_office_postal_code: "",
  director_info: { first_name: "", last_name: "", birth_date: "", nationality: "Française" },
  fiscal_option_is: true, fiscal_option_tva: true,
  current_step: 1, steps_completed: [], documents: {}, status: "draft",
};

export default function CompanyCreationDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState<CreationData>(defaultData);
  const [activeStep, setActiveStep] = useState(1);
  const [hasProject, setHasProject] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    loadProject();
  }, [user]);

  const loadProject = async () => {
    const { data: rows } = await supabase
      .from("company_creation")
      .select("*")
      .eq("user_id", user!.id)
      .order("created_at", { ascending: false })
      .limit(1);
    if (rows && rows.length > 0) {
      const row = rows[0];
      setData({
        id: row.id,
        company_name: row.company_name || "",
        business_activity: row.business_activity || "",
        legal_form: row.legal_form || "",
        capital_amount: Number(row.capital_amount) || 1000,
        capital_distribution: (row.capital_distribution as any) || [],
        head_office_address: row.head_office_address || "",
        head_office_city: row.head_office_city || "",
        head_office_postal_code: row.head_office_postal_code || "",
        director_info: (row.director_info as any) || defaultData.director_info,
        fiscal_option_is: row.fiscal_option_is ?? true,
        fiscal_option_tva: row.fiscal_option_tva ?? true,
        current_step: row.current_step || 1,
        steps_completed: (row.steps_completed as any) || [],
        documents: (row.documents as any) || {},
        status: row.status || "draft",
      });
      setActiveStep(row.current_step || 1);
      setHasProject(true);
    }
    setLoading(false);
  };

  const saveProject = async (newData?: Partial<CreationData>) => {
    if (!user) return;
    setSaving(true);
    const toSave = { ...data, ...newData };
    const payload = {
      user_id: user.id,
      company_name: toSave.company_name,
      business_activity: toSave.business_activity,
      legal_form: toSave.legal_form,
      capital_amount: toSave.capital_amount,
      capital_distribution: toSave.capital_distribution as any,
      head_office_address: toSave.head_office_address,
      head_office_city: toSave.head_office_city,
      head_office_postal_code: toSave.head_office_postal_code,
      director_info: toSave.director_info as any,
      fiscal_option_is: toSave.fiscal_option_is,
      fiscal_option_tva: toSave.fiscal_option_tva,
      current_step: toSave.current_step,
      steps_completed: toSave.steps_completed as any,
      documents: toSave.documents as any,
      status: toSave.status,
    };
    if (data.id) {
      await supabase.from("company_creation").update(payload).eq("id", data.id);
    } else {
      const { data: inserted } = await supabase.from("company_creation").insert(payload).select().single();
      if (inserted) setData(prev => ({ ...prev, id: inserted.id }));
      setHasProject(true);
    }
    setSaving(false);
    toast({ title: "Sauvegardé ✓" });
  };

  const updateField = (field: string, value: any) => {
    setData(prev => ({ ...prev, [field]: value }));
  };

  const completeStep = (step: number) => {
    const completed = data.steps_completed.includes(step)
      ? data.steps_completed
      : [...data.steps_completed, step];
    const nextStep = Math.min(step + 1, 8);
    const newData = { steps_completed: completed, current_step: nextStep };
    setData(prev => ({ ...prev, ...newData }));
    setActiveStep(nextStep);
    saveProject(newData);
  };

  const createNewProject = () => {
    setData(defaultData);
    setActiveStep(1);
    setHasProject(false);
    saveProject({ ...defaultData });
  };

  const progress = Math.round((data.steps_completed.length / 8) * 100);

  if (loading) {
    return (
      <SidebarProvider>
        <div className="min-h-screen flex w-full bg-background">
          <DashboardSidebar />
          <main className="flex-1 flex items-center justify-center">
            <div className="animate-pulse text-muted-foreground">Chargement...</div>
          </main>
        </div>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <DashboardSidebar />
        <main className="flex-1 overflow-y-auto">
          <div className="p-4 md:p-8 max-w-6xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <SidebarTrigger />
                <div>
                  <div className="flex items-center gap-2">
                    <h1 className="text-2xl font-bold text-foreground">Création d'entreprise</h1>
                    <Badge variant="secondary" className="text-xs">
                      {data.status === "draft" ? "Brouillon" : data.status === "completed" ? "Terminé" : "En cours"}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">Immatriculez votre entreprise pas à pas</p>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={createNewProject} className="gap-2">
                <Plus className="h-4 w-4" /> Nouveau projet
              </Button>
            </div>

            {/* MacBook Animation */}
            <MacBookAnimation />

            {/* Progress bar */}
            <Card className="border-primary/20">
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-foreground">Progression globale</span>
                  <span className="text-sm font-bold text-primary">{progress}%</span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-gradient-to-r from-primary to-primary/70 rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Roadmap visuel */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {STEPS.map((step) => {
                const isCompleted = data.steps_completed.includes(step.id);
                const isActive = activeStep === step.id;
                return (
                  <motion.div
                    key={step.id}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Card
                      className={`cursor-pointer transition-all border-2 ${
                        isActive ? "border-primary bg-primary/5 shadow-md" :
                        isCompleted ? "border-green-500/40 bg-green-500/5" :
                        "border-border hover:border-primary/30"
                      }`}
                      onClick={() => setActiveStep(step.id)}
                    >
                      <CardContent className="p-3 text-center space-y-1">
                        <div className={`mx-auto h-10 w-10 rounded-full flex items-center justify-center ${
                          isCompleted ? "bg-green-500/20 text-green-600" :
                          isActive ? "bg-primary/20 text-primary" :
                          "bg-muted text-muted-foreground"
                        }`}>
                          {isCompleted ? <CheckCircle2 className="h-5 w-5" /> : <step.icon className="h-5 w-5" />}
                        </div>
                        <p className="text-xs font-medium truncate">{step.label}</p>
                        <p className="text-[10px] text-muted-foreground">{step.description}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>

            {/* Formulaire de l'étape active */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      {(() => { const S = STEPS[activeStep - 1]; return <S.icon className="h-5 w-5 text-primary" />; })()}
                      Étape {activeStep} — {STEPS[activeStep - 1].label}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {activeStep === 1 && (
                      <>
                        <div className="space-y-2">
                          <Label>Nom de l'entreprise</Label>
                          <Input value={data.company_name} onChange={e => updateField("company_name", e.target.value)} placeholder="Ex: Grafton IA" />
                        </div>
                        <div className="space-y-2">
                          <Label>Activité principale</Label>
                          <Textarea value={data.business_activity} onChange={e => updateField("business_activity", e.target.value)} placeholder="Décrivez votre activité en quelques lignes..." rows={3} />
                        </div>
                      </>
                    )}

                    {activeStep === 2 && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {LEGAL_FORMS.map(lf => (
                          <Card
                            key={lf.value}
                            className={`cursor-pointer transition-all border-2 ${
                              data.legal_form === lf.value ? "border-primary bg-primary/5" : "border-border hover:border-primary/30"
                            }`}
                            onClick={() => updateField("legal_form", lf.value)}
                          >
                            <CardContent className="p-4">
                              <div className="flex items-center gap-3">
                                <div className={`h-8 w-8 rounded-lg flex items-center justify-center text-xs font-bold ${
                                  data.legal_form === lf.value ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                                }`}>{lf.label.slice(0, 2)}</div>
                                <div>
                                  <p className="font-medium text-sm">{lf.label}</p>
                                  <p className="text-xs text-muted-foreground">{lf.desc}</p>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                        <Card className="border-dashed border-2 border-primary/30 bg-primary/5 col-span-full">
                          <CardContent className="p-4 flex items-center gap-3">
                            <Sparkles className="h-5 w-5 text-primary" />
                            <div>
                              <p className="text-sm font-medium text-primary">Besoin d'aide ?</p>
                              <p className="text-xs text-muted-foreground">L'IA Grafton peut vous recommander la forme juridique idéale selon votre projet</p>
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    )}

                    {activeStep === 3 && (
                      <>
                        <div className="space-y-2">
                          <Label>Montant du capital social (€)</Label>
                          <Input type="number" value={data.capital_amount} onChange={e => updateField("capital_amount", Number(e.target.value))} />
                        </div>
                        <div className="p-4 rounded-lg border border-dashed border-primary/30 bg-primary/5">
                          <div className="flex items-center gap-2 mb-2">
                            <Landmark className="h-4 w-4 text-primary" />
                            <span className="text-sm font-medium text-primary">Dépôt de capital — Qonto</span>
                          </div>
                          <p className="text-xs text-muted-foreground mb-3">Déposez votre capital directement via notre partenaire Qonto (intégration bientôt disponible)</p>
                          <Button variant="outline" size="sm" disabled className="gap-2">
                            <BriefcaseBusiness className="h-4 w-4" /> Connexion Qonto — Bientôt
                          </Button>
                        </div>
                      </>
                    )}

                    {activeStep === 4 && (
                      <>
                        <div className="space-y-2">
                          <Label>Adresse du siège social</Label>
                          <Input value={data.head_office_address} onChange={e => updateField("head_office_address", e.target.value)} placeholder="123 rue de la Paix" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-2">
                            <Label>Ville</Label>
                            <Input value={data.head_office_city} onChange={e => updateField("head_office_city", e.target.value)} placeholder="Paris" />
                          </div>
                          <div className="space-y-2">
                            <Label>Code postal</Label>
                            <Input value={data.head_office_postal_code} onChange={e => updateField("head_office_postal_code", e.target.value)} placeholder="75001" />
                          </div>
                        </div>
                      </>
                    )}

                    {activeStep === 5 && (
                      <>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-2">
                            <Label>Prénom</Label>
                            <Input value={data.director_info.first_name} onChange={e => updateField("director_info", { ...data.director_info, first_name: e.target.value })} />
                          </div>
                          <div className="space-y-2">
                            <Label>Nom</Label>
                            <Input value={data.director_info.last_name} onChange={e => updateField("director_info", { ...data.director_info, last_name: e.target.value })} />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-2">
                            <Label>Date de naissance</Label>
                            <Input type="date" value={data.director_info.birth_date} onChange={e => updateField("director_info", { ...data.director_info, birth_date: e.target.value })} />
                          </div>
                          <div className="space-y-2">
                            <Label>Nationalité</Label>
                            <Input value={data.director_info.nationality} onChange={e => updateField("director_info", { ...data.director_info, nationality: e.target.value })} />
                          </div>
                        </div>
                      </>
                    )}

                    {activeStep === 6 && (
                      <>
                        <div className="flex items-center justify-between p-4 rounded-lg border">
                          <div>
                            <p className="font-medium text-sm">Impôt sur les Sociétés (IS)</p>
                            <p className="text-xs text-muted-foreground">Recommandé pour les sociétés (SAS, SARL)</p>
                          </div>
                          <Switch checked={data.fiscal_option_is} onCheckedChange={v => updateField("fiscal_option_is", v)} />
                        </div>
                        <div className="flex items-center justify-between p-4 rounded-lg border">
                          <div>
                            <p className="font-medium text-sm">Assujetti à la TVA</p>
                            <p className="text-xs text-muted-foreground">Franchise en base si CA &lt; seuil</p>
                          </div>
                          <Switch checked={data.fiscal_option_tva} onCheckedChange={v => updateField("fiscal_option_tva", v)} />
                        </div>
                      </>
                    )}

                    {activeStep === 7 && (
                      <div className="space-y-3">
                        {[
                          { key: "id_card", label: "Pièce d'identité", icon: FileCheck },
                          { key: "proof_address", label: "Justificatif de domicile", icon: MapPin },
                          { key: "statuts", label: "Statuts signés", icon: FileText },
                          { key: "capital_cert", label: "Attestation de dépôt de capital", icon: Landmark },
                          { key: "m0_form", label: "Formulaire M0 / P0", icon: FileText },
                        ].map(doc => (
                          <div key={doc.key} className="flex items-center justify-between p-3 rounded-lg border">
                            <div className="flex items-center gap-3">
                              <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${
                                data.documents[doc.key] ? "bg-green-500/20 text-green-600" : "bg-muted text-muted-foreground"
                              }`}>
                                <doc.icon className="h-4 w-4" />
                              </div>
                              <div>
                                <p className="text-sm font-medium">{doc.label}</p>
                                <p className="text-xs text-muted-foreground">
                                  {data.documents[doc.key] ? "✓ Téléchargé" : "Non fourni"}
                                </p>
                              </div>
                            </div>
                            <Button variant="outline" size="sm" className="gap-1">
                              <Upload className="h-3 w-3" /> Importer
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}

                    {activeStep === 8 && (
                      <div className="text-center space-y-4 py-6">
                        <div className="mx-auto h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                          <Building2 className="h-8 w-8 text-primary" />
                        </div>
                        <h3 className="text-lg font-bold">Prêt pour l'immatriculation</h3>
                        <p className="text-sm text-muted-foreground max-w-md mx-auto">
                          Vérifiez que toutes les étapes sont complétées. Grafton IA vous accompagne pour le dépôt au Greffe via le Guichet Unique (INPI).
                        </p>
                        <div className="flex flex-col items-center gap-2">
                          <Button className="gap-2" onClick={() => window.open("https://procedures.inpi.fr", "_blank")}>
                            <ArrowRight className="h-4 w-4" /> Accéder au Guichet Unique INPI
                          </Button>
                          <p className="text-xs text-muted-foreground">formalites.entreprises.gouv.fr</p>
                        </div>
                      </div>
                    )}

                    {/* Navigation */}
                    <div className="flex items-center justify-between pt-4 border-t">
                      <Button variant="ghost" size="sm" disabled={activeStep === 1} onClick={() => setActiveStep(s => s - 1)} className="gap-1">
                        <ChevronLeft className="h-4 w-4" /> Précédent
                      </Button>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => saveProject()} disabled={saving}>
                          {saving ? "..." : "Sauvegarder"}
                        </Button>
                        <Button size="sm" onClick={() => completeStep(activeStep)} className="gap-1">
                          {activeStep === 8 ? "Terminer" : "Valider & continuer"} <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </AnimatePresence>
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}
